/**
 * CetusEd Admin Console — District Detail View
 *
 * COMPLIANCE: FERPA §1232g(b)(4)(B) — Access to a specific district's data
 *   requires RBAC scope 'admin:district:read:{districtId}'. This view shows
 *   district configuration, usage statistics, and linked services.
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDistrict } from '../../api/hooks';

export const DistrictDetail: React.FC = () => {
  const { districtId } = useParams<{ districtId: string }>();
  const { data: district, isLoading, error } = useDistrict(districtId || '');

  if (isLoading) return <div className="admin-loading">Loading district...</div>;

  if (error || !district) {
    return (
      <div className="admin-error" role="alert">
        District not found or you do not have access.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/districts">Districts</Link> / {district.name}
      </nav>

      <div className="page-header">
        <h1>{district.name}</h1>
        <span className={`status-badge status-badge--${district.status}`}>
          {district.status}
        </span>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3>Configuration</h3>
          <dl>
            <dt>State</dt>
            <dd>{district.state}</dd>
            <dt>Deployment Mode</dt>
            <dd>{district.deploymentMode}</dd>
            <dt>District ID</dt>
            <dd><code>{district.id}</code></dd>
            <dt>Created</dt>
            <dd>{new Date(district.createdAt).toLocaleDateString()}</dd>
          </dl>
        </div>

        <div className="detail-card">
          <h3>Usage</h3>
          <dl>
            <dt>Users</dt>
            <dd>{district.userCount.toLocaleString()}</dd>
            <dt>Students</dt>
            <dd>{district.studentCount.toLocaleString()}</dd>
            <dt>Storage Used</dt>
            <dd>{(district.storageUsedBytes / 1024 / 1024).toFixed(1)} MB</dd>
          </dl>
        </div>
      </div>

      <div className="detail-actions">
        <Link to={`/districts/${district.id}/audit`} className="admin-btn admin-btn--secondary">
          View Audit Log
        </Link>
        <Link to="/billing" className="admin-btn admin-btn--secondary">
          Billing
        </Link>
      </div>
    </div>
  );
};
